import { Hero } from "@/components/site/Hero";
import { Marquee } from "@/components/site/Marquee";
import { Products } from "@/components/site/Products";
import { Manifesto } from "@/components/site/Manifesto";
import { Impact } from "@/components/site/Impact";
import { AirflowCalculator } from "@/components/site/AirflowCalculator";
import { WhirlybirdCompare } from "@/components/site/WhirlybirdCompare";
import { InstallGallery } from "@/components/site/InstallGallery";
import { Faq } from "@/components/site/Faq";
import { QuoteFooter } from "@/components/site/QuoteFooter";
import { IMG, CHAPTERS, STATS, FAQS, PRODUCT_OPTIONS } from "@/components/site/data";
import { scrollToId, Reveal } from "@/components/site/motion";
import { Seo } from "@/components/site/Seo";
import { Sun, VolumeX, Thermometer, PlugZap, Calculator } from "lucide-react";

const QUICK_FACTS = [
  { icon: Sun, title: "100% Solar Powered", text: "No wiring, no switchboard, no electrician — the sun does all the work." },
  { icon: Thermometer, title: "Up to 30°C Cooler", text: "Extracts superheated air from your roof cavity all day long." },
  { icon: PlugZap, title: "Zero Running Costs", text: "Nothing to plug in and nothing on your power bill — ever." },
  { icon: VolumeX, title: "Whisper Quiet", text: "Brushless motor technology you'll never hear from inside." },
];

const QuickIntro = () => (
  <section data-testid="vent-quick-intro" className="relative py-20 lg:py-28 bg-[#010101]">
    <div className="max-w-7xl mx-auto px-6 lg:px-12">
      <Reveal>
        <div className="flex flex-col sm:flex-row sm:items-center gap-6 mb-14">
          <p className="text-zinc-400 text-lg leading-relaxed max-w-3xl">
            A <span className="text-white font-semibold">SolarArk solar roof ventilator</span> uses free energy from the sun to continuously pump hot, stale air out of your roof cavity — replacing it with cooler outside air drawn through the eaves. Traditional passive wind vents move only around <span className="text-[#fcc41b] font-semibold">100 m³/hour on a windy day</span> — a single SolarArk unit moves thousands, sun-up to sun-down.
          </p>
          <button
            onClick={() => scrollToId("calculator")}
            data-testid="intro-calculator-link"
            className="shrink-0 inline-flex items-center gap-2 rounded-full border border-[#fcc41b]/50 text-[#fcc41b] font-display font-semibold px-6 py-3 hover:bg-[#fcc41b] hover:text-black transition-colors duration-300"
          >
            <Calculator size={17} />
            Try the airflow calculator
          </button>
        </div>
      </Reveal>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {QUICK_FACTS.map((f, i) => (
          <Reveal key={f.title} delay={i * 0.07}>
            <div className="h-full rounded-3xl border border-[#262626] bg-[#0d0d0d] p-7 hover:border-[#fcc41b]/40 transition-colors duration-500">
              <f.icon size={24} className="text-[#fcc41b] mb-5" />
              <h3 className="font-display font-semibold text-white text-lg mb-2">{f.title}</h3>
              <p className="text-zinc-400 text-sm leading-relaxed">{f.text}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

export default function VentilationPage() {
  const faqLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: FAQS.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };
  return (
    <>
      <Seo
        path="/solar-roof-ventilation"
        title="Solar Roof Ventilation — A Superior Whirlybird Alternative"
        description="Looking for whirlybirds? SolarArk solar roof ventilators are the superior alternative — one unit replaces up to 52 whirlybirds, moving thousands of m³/hr at zero running cost. Whisper-quiet, vermin proof and totally solar powered."
        jsonLd={faqLd}
      />
      <Hero
        overline="Solar Roof Ventilation · Est. 2009"
        line1="Feeling the heat?"
        line2Prefix="Make your roof"
        line2Highlight="30°C cooler."
        subtitle={<>Australia's best-performing <span className="text-white font-semibold">solar roof ventilators</span> — whisper-quiet, vermin proof and totally solar powered, with <span className="text-white font-semibold">zero running costs, ever.</span></>}
        bgImage={IMG.ventHero}
        secondaryLabel="Explore Range"
        onSecondary={() => scrollToId("products")}
      />
      <Marquee />
      <QuickIntro />
      <Products />
      <AirflowCalculator />
      <Manifesto
        overline="The SolarArk Advantage"
        title="Australia's best performer."
        subtitle="Four reasons over 30,000 Australian homes and businesses chose SolarArk solar roof ventilation."
        chapters={CHAPTERS}
      />
      <WhirlybirdCompare />
      <Impact overline="The Numbers" title="Serious performance. Zero running costs." stats={STATS} />
      <InstallGallery />
      <Faq title="Ventilation, answered." faqs={FAQS} />
      <QuoteFooter productOptions={PRODUCT_OPTIONS} blurb="Tell us about your roof and our team will recommend the right SolarArk ventilator and confirm sizing. Install it yourself the DIY way, or we'll connect you with our national installer network — no obligation." />
    </>
  );
}
